import { CookieHelperService } from './../../service/cookie-helper.service';
import { Component, Input, OnInit, ContentChild, ElementRef, OnDestroy } from '@angular/core';
import { FeatureCodes, UserPreferenceType } from '../../constants';
import { FeatureConfig, FeatureConfigService } from '../../service/feature-config.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-feature-lock',
  template: `
    <div
      class="feature-lock-wrapper"
      [class.is-disabled]="isDisabled"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
    >
      <div class="feature-content-wrapper">
        <ng-content></ng-content>
      </div>

      <div
        class="upgrade-overlay"
        *ngIf="isDisabled && config.popupSize === 'large' && !globalHidePrompt && showPopup"
        (mouseenter)="onPopupMouseEnter()"
        (mouseleave)="onPopupMouseLeave()"
      >
        <div class="large-popup-overlay"></div>
        <div class="upgrade-card">
          <div class="popup-header">
            <app-crown-icon [size]="20" [showBadge]="false" [customClass]="'popup-icon'">
            </app-crown-icon>
            <span class="popup-title">{{ config.title }}</span>
          </div>
          <p class="popup-description">
            {{ config.compactDescription || config.description }}
          </p>
          <div class="popup-actions" [class.single-button]="!config.learnMoreLink">
            <button class="popup-upgrade-btn" (click)="onUpgradeClick()">Upgrade</button>
            <a
              href="#"
              class="popup-learn-more"
              *ngIf="config.showLearnMore && config.learnMoreLink"
              (click)="onLearnMoreClick($event)"
              >Learn More</a
            >
          </div>
          <div class="checkbox-container" *ngIf="showHidePromptOption">
            <label class="hide-prompt-checkbox">
              <input
                type="checkbox"
                [(ngModel)]="hidePromptChecked"
                (change)="onHidePromptChange()"
              />
              Don't show again for any features
            </label>
          </div>
        </div>
      </div>

      <div
        class="compact-popup"
        *ngIf="isDisabled && config.popupSize === 'compact' && !globalHidePrompt && showPopup"
        [ngClass]="'position-' + popupPosition"
        (mouseenter)="onPopupMouseEnter()"
        (mouseleave)="onPopupMouseLeave()"
      >
        <div class="popup-header">
          <app-crown-icon [size]="20" [showBadge]="false" [customClass]="'popup-icon'">
          </app-crown-icon>
          <span class="popup-title">{{ config.title }}</span>
        </div>
        <p class="popup-description">
          {{ config.compactDescription || config.description }}
        </p>
        <div class="popup-actions" [class.single-button]="!config.learnMoreLink">
          <button class="popup-upgrade-btn" (click)="onUpgradeClick()">Upgrade</button>
          <a
            href="#"
            class="popup-learn-more"
            *ngIf="config.showLearnMore && config.learnMoreLink"
            (click)="onLearnMoreClick($event)"
            >Learn More</a
          >
        </div>
        <div class="checkbox-container" *ngIf="showHidePromptOption">
          <label class="hide-prompt-checkbox">
            <input
              type="checkbox"
              [(ngModel)]="hidePromptChecked"
              (change)="onHidePromptChange()"
            />
            Don't show again for any features
          </label>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./feature-lock.component.scss'],
})
export class FeatureLockComponent implements OnInit, OnDestroy {
  @Input() featureCode!: FeatureCodes;
  @Input() isDisabled: boolean = false;
  @Input() showProBadge: boolean = true;
  @Input() disabledStyle: 'blur' | 'opacity' = 'blur';
  @Input() popupPosition:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right' = 'bottom';
  @Input() showHidePromptOption: boolean = true;
  @Input() popupHideDelay: number = 300; // 1 second delay by default

  @ContentChild('featureHeader', { read: ElementRef }) featureHeader?: ElementRef;

  config: FeatureConfig = {} as FeatureConfig;
  hidePromptChecked: boolean = false;
  globalHidePrompt: boolean = false;
  showPopup: boolean = false;

  private destroy$ = new Subject<void>();
  private storageEventListener: any;
  private hidePopupTimeout: any;
  private isHoveringFeature: boolean = false;
  private isHoveringPopup: boolean = false;
  constructor(
    private featureConfigService: FeatureConfigService,
    private toaster: ToastrService,
    private cookieHelperService: CookieHelperService
  ) {}

  ngOnInit(): void {
    if (this.featureCode) {
      this.config = this.featureConfigService.getFeatureConfig(this.featureCode);
      this.globalHidePrompt = this.getGlobalPromptPreference();

      this.featureConfigService
        .getGlobalHidePromptObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe((val: boolean) => {
          this.globalHidePrompt = val;
        });

      this.storageEventListener = (event: StorageEvent) => {
        if (event.key === 'globalHideUpgradePopup') {
          this.globalHidePrompt = event.newValue === 'true';
          this.featureConfigService.setHidePromptGlobally(this.globalHidePrompt);
        }
      };
      window.addEventListener('storage', this.storageEventListener);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }

    if (this.hidePopupTimeout) {
      clearTimeout(this.hidePopupTimeout);
    }
  }

  onMouseEnter(): void {
    this.isHoveringFeature = true;
    this.clearHideTimeout();
    this.showPopup = true;
  }

  onMouseLeave(): void {
    this.isHoveringFeature = false;
    this.scheduleHidePopup();
  }

  onPopupMouseEnter(): void {
    this.isHoveringPopup = true;
    this.clearHideTimeout();
  }

  onPopupMouseLeave(): void {
    this.isHoveringPopup = false;
    this.scheduleHidePopup();
  }

  private scheduleHidePopup(): void {
    // Clear any existing timeout first
    this.clearHideTimeout();

    // Only schedule hide if neither feature nor popup is being hovered
    if (!this.isHoveringFeature && !this.isHoveringPopup) {
      this.hidePopupTimeout = setTimeout(() => {
        // Double-check the hover states before hiding
        if (!this.isHoveringFeature && !this.isHoveringPopup) {
          this.showPopup = false;
        }
      }, this.popupHideDelay);
    }
  }

  private clearHideTimeout(): void {
    if (this.hidePopupTimeout) {
      clearTimeout(this.hidePopupTimeout);
      this.hidePopupTimeout = null;
    }
  }

  onUpgradeClick(): void {
    window.open(
      'https://piqet.com/sustainable-packaging-lca-calculator#license-pricelists',
      '_blank'
    );
  }

  onLearnMoreClick(event: Event): void {
    event.preventDefault();

    if (this.config.learnMoreLink) {
      // Open the learn more link in a new tab
      window.open(this.config.learnMoreLink, '_blank');
    }
  }

  onHidePromptChange(): void {
    this.featureConfigService
      .saveUserPreference(UserPreferenceType.UpgradePlanPopup, this.hidePromptChecked)
      .then((res: any) => {
        if (res.data) {
          this.toaster.success('Popup preferences updated for all features');
          this.setGlobalPromptPreference();
          this.globalHidePrompt = this.hidePromptChecked;
          this.featureConfigService.setHidePromptGlobally(this.hidePromptChecked);
        } else {
          this.toaster.error(res.message);
          this.hidePromptChecked = false;
        }
      })
      .catch(error => {
        console.error('Failed to save preference:', error);
        this.toaster.error('Failed to save preference');
        this.hidePromptChecked = false;
      });
  }

  private getGlobalPromptPreference(): boolean {
    const globalPref = this.cookieHelperService.getGlobalUpgradePopupPreference();
    return globalPref === 'true';
  }

  private setGlobalPromptPreference(): void {
    const oneYearFromNow = new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000);
    this.cookieHelperService.setGlobalUpgradePopupPreference(
      this.hidePromptChecked,
      oneYearFromNow
    );
  }
}
